/**
 * Utility functions for processing HTML content
 */

/**
 * Processes HTML content and adds wrapper div to all tables
 * to provide horizontal scroll functionality
 *
 * @param htmlContent - HTML content to be processed
 * @param wrapperClass - CSS class for wrapper div (default: 'table-wrapper overflow-x-auto')
 * @returns Processed HTML content with table wrapper
 */
export const processTableContent = (
  htmlContent: string,
  wrapperClass: string = 'table-wrapper overflow-x-auto',
): string => {
  if (!htmlContent) {
    return '';
  }

  const tableRegex = /<table[^>]*>([\s\S]*?)<\/table>/gi;

  return htmlContent.replace(tableRegex, (match) => {
    return `<div class="${wrapperClass}">${match}</div>`;
  });
};

/**
 * Processes HTML content and adds wrapper to specific elements
 *
 * @param htmlContent - HTML content to be processed
 * @param elementTag - HTML tag to be wrapped (example: 'table', 'img', 'video')
 * @param wrapperClass - CSS class for wrapper div
 * @returns Processed HTML content with wrapper
 */
export const processElementWrapper = (
  htmlContent: string,
  elementTag: string,
  wrapperClass: string,
): string => {
  if (!htmlContent || !elementTag) {
    return htmlContent || '';
  }

  const elementRegex = new RegExp(`<${elementTag}[^>]*>[\\s\\S]*?<\\/${elementTag}>`, 'gi');

  return htmlContent.replace(elementRegex, (match) => {
    return `<div class="${wrapperClass}">${match}</div>`;
  });
};

/**
 * Processes HTML content for multiple wrappers at once
 *
 * @param htmlContent - HTML content to be processed
 * @param processors - Array of processor configurations
 * @returns Processed HTML content
 */
export const processMultipleWrappers = (
  htmlContent: string,
  processors: Array<{
    elementTag: string;
    wrapperClass: string;
  }>,
): string => {
  if (!htmlContent || !processors.length) {
    return htmlContent || '';
  }

  return processors.reduce((processedContent, processor) => {
    return processElementWrapper(processedContent, processor.elementTag, processor.wrapperClass);
  }, htmlContent);
};
